import { Row } from '@tanstack/react-table';
import { TableCell } from '@carbon/react';
import { TractNRIColumns } from 'components/nri-table/types';
import {
  PackageEvaluationAllocationResponse,
  PackageEvaluationSlotAllocationsResponse,
} from 'types/packages-api/responses';
import { MapPoints } from './map-isolator';

// Re-export shared draggable utilities for backwards compatibility
export {
  getPinningStyles,
  draggingStyle,
  handleDragEnd,
  handleDragMove,
  getInitialColumnOrder,
} from 'components/table/draggable';
export type {
  DragHeaderProps,
  DragCellProps,
} from 'components/table/draggable';

const getTotalCells = (
  headersList: { key: string }[],
  cellContentMapping: { [key: string]: JSX.Element },
  className: string
) => {
  const totalCells: JSX.Element[] = [];
  headersList.forEach(({ key }, i) => {
    const cellContent = cellContentMapping[key] || (
      <TableCell key={`empty-total-${i}`} className={className} />
    );
    totalCells.push(cellContent);
  });
  return totalCells;
};

const buildMapPointsFromUnits = (
  allocations: PackageEvaluationAllocationResponse[]
) => {
  const devAreasMap: Record<string, boolean> = {};
  return allocations.reduce((acc, prev) => {
    const assetName = prev.asset.name;
    const devArea = prev.developmentArea;
    const devAreaName = devArea.name;
    if (!assetName || !devArea) return acc;
    if (!devAreaName) return acc;

    if (acc[assetName]) {
      if (!devAreasMap[`${assetName}-${devAreaName}`]) {
        const devAreaWithWells = {
          ...devArea,
          wells: [],
        };
        acc[assetName].push(devAreaWithWells);
        devAreasMap[`${assetName}-${devAreaName}`] = true;
      }
    } else {
      const devAreaWithWells = {
        ...devArea,
        wells: [],
      };
      acc[assetName] = [devAreaWithWells];
      devAreasMap[`${assetName}-${devAreaName}`] = true;
    }
    return acc;
  }, {} as MapPoints);
};

const buildMapPointsFromSlots = (
  allocations: PackageEvaluationSlotAllocationsResponse[]
) => {
  const devAreasMap: Record<string, boolean> = {};
  return allocations.reduce((acc, prev) => {
    const assetName = prev.asset.name;
    const devArea = prev.developmentArea;
    const devAreaName = devArea.name;
    const uwi = prev.well?.uwi;
    const well = uwi
      ? {
          uwi,
          operator: prev.operator.name,
          name: prev.well?.name ?? null,
        }
      : undefined;
    if (!assetName || !devArea) return acc;
    if (!devAreaName) return acc;

    if (acc[assetName]) {
      if (!devAreasMap[`${assetName}-${devAreaName}`]) {
        const devAreaWithWells = {
          ...devArea,
          wells: well ? [well] : [],
        };
        acc[assetName].push(devAreaWithWells);
        devAreasMap[`${assetName}-${devAreaName}`] = true;
      } else {
        const devAreaObj = acc[assetName].find((el) => el.name === devAreaName);
        if (devAreaObj && uwi) {
          const index = devAreaObj.wells.findIndex((el) => el.uwi === uwi);
          if (index < 0 && well) devAreaObj.wells.push(well);
        }
      }
    } else {
      const devAreaWithWells = {
        ...devArea,
        wells: well ? [well] : [],
      };
      acc[assetName] = [devAreaWithWells];
      devAreasMap[`${assetName}-${devAreaName}`] = true;
    }
    return acc;
  }, {} as MapPoints);
};

type RangeTuple = [string | undefined, string | undefined];

export type Range = RangeTuple | undefined;

interface FilterFunctions {
  percentageRange: <TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: Range
  ) => boolean;
  decimalRangeToFixed: <TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: Range
  ) => boolean;
  decimalRange: <TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: Range
  ) => boolean;
}

const baseRangeFilter = <TData,>(
  row: Row<TData>,
  columnId: string,
  filterValue: Range,
  transformValue: (value: number) => number
) => {
  if (!filterValue) return true;

  const [min, max] = filterValue;

  // When either value is empty string or both are empty/undefined, show all rows
  if (min === '' || max === '' || (min === undefined && max === undefined)) {
    return true;
  }

  const value = row.getValue(columnId);

  // Allow '--' as a valid case for null values
  if (value === '--') return true;

  // Type check first before null check
  if (typeof value !== 'number') return false;

  const transformedValue = transformValue(value);

  // Range filtering
  if (min && max) {
    return transformedValue >= Number(min) && transformedValue <= Number(max);
  }

  if (min) {
    return transformedValue >= Number(min);
  }

  if (max) {
    return transformedValue <= Number(max);
  }

  return true;
};

// Create filter functions for percentage and decimal range
export const filterFunctions: FilterFunctions = {
  // For percentage values that are stored as decimals (e.g. 0.4560 displays as 45.60%)
  // Display uses PERCENT_FORMATTER_TWO_DECIMALS.format(value)
  percentageRange: <TData,>(
    row: Row<TData>,
    columnId: string,
    filterValue: Range
  ) =>
    baseRangeFilter(row, columnId, filterValue, (value) =>
      Number((value * 100).toFixed(2))
    ),

  // For decimal values that need specific precision based on the field type
  // NRI/NWI display uses toFixed(value, 8)
  decimalRangeToFixed: <TData,>(
    row: Row<TData>,
    columnId: string,
    filterValue: Range
  ) =>
    baseRangeFilter(row, columnId, filterValue, (value) => {
      const toFixedOffset = [
        'netRevenueInterest', // Uses toFixed(value, 8) in display
        'netWorkingInterest', // Uses toFixed(value, 8) in display
      ].includes(columnId)
        ? 8
        : 2;
      return Number(Number(value).toFixed(toFixedOffset));
    }),

  // For large decimal values that are displayed rounded to whole numbers
  // Display uses DECIMAL_FORMATTER.format(value)
  // Used for values like depths, volumes, etc.
  decimalRange: <TData,>(
    row: Row<TData>,
    columnId: string,
    filterValue: Range
  ) =>
    baseRangeFilter(row, columnId, filterValue, (value) =>
      Number(value.toFixed(0))
    ),
};

type RangeFilterType =
  'percentageRange' | 'decimalRangeToFixed' | 'decimalRange';

// create filter function based on transformation type
export const createFilterFn = (transformationType: RangeFilterType) => {
  switch (transformationType) {
    case 'percentageRange':
      return filterFunctions.percentageRange;
    case 'decimalRangeToFixed':
      return filterFunctions.decimalRangeToFixed;
    case 'decimalRange':
      return filterFunctions.decimalRange;
  }
};

/**
 * Column keys for the Enhanced Overview Slots table.
 * Includes volume source columns for Eval 3.0.
 * Order: slotId, volumeSourceId, then all new volume source columns, then remaining columns.
 */
export const ENHANCED_OVERVIEW_SLOT_COLUMNS: Array<TractNRIColumns> = [
  'slotId',
  'assetType',
  'volumeSourceId',
  'forecastStatus',
  'forecastDate',
  'dcaScenario',
  'slotLength',
  'typeCurveAreaName',
  'typeCurveLateralLength',
  'afeSourceId',
  'afeWellName',
  'slotContingenciesCosts',
  'contingenciesCostsDate',
  'slotFacilitiesCosts',
  'facilitiesCostsDate',
  'slotDrillingCosts',
  'drillingCostsDate',
  'slotCompletionCosts',
  'completionCostsDate',
  'slotOtherCosts',
  'otherCostsDate',
  'unit',
  // Per-row pricing (Eval 3.0). Sit immediately before the NRI/WI columns.
  'targetNpvExcluded',
  'maxNpvExcluded',
  'targetNpvIncluded',
  'maxNpvIncluded',
  'totalSlotNri',
  'totalSlotWi',
  'interestType',
  'unitSource',
  'unitDevelopmentType',
  // Scenario-area columns (populated when viewing a completed eval calculation)
  'dpsArea',
  'taxArea',
  'differentialArea',
  'discountRateArea',
  'expenseArea',
  'wellName',
  'wellStatus',
  'typeCurveStatus',
  'operator',
  'holeDirection',
  'formation',
  'stateAbbreviation',
  'basin',
  'tier',
  'trueVerticalDepthFeet',
  'slotNumber',
  'permitDate',
  'spudDate',
  'rigReleaseDate',
  'completionDate',
  'firstProductionDate',
  'shutInDate',
  'valued',
  'unvaluedReason',
];

/**
 * AFE-related column keys that should be hidden when scenario mode is selected.
 * Shared between SlotsContent and SlotAllocationsTab.
 */
export const AFE_COLUMNS: Set<TractNRIColumns> = new Set([
  'afeSourceId',
  'afeWellName',
  'slotContingenciesCosts',
  'contingenciesCostsDate',
  'slotFacilitiesCosts',
  'facilitiesCostsDate',
  'slotDrillingCosts',
  'drillingCostsDate',
  'slotCompletionCosts',
  'completionCostsDate',
  'slotOtherCosts',
  'otherCostsDate',
]);

export { buildMapPointsFromSlots, buildMapPointsFromUnits, getTotalCells };
